import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, Clock, ChevronRight, List, Calendar as CalendarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import RotatingText from '../components/common/RotatingText';
import TournamentsCalendar from '../components/common/TournamentsCalendar';
import { getUpcomingTournaments } from '../services/tournamentService';

const FixturesPage = () => {
  const { isDarkMode } = useTheme();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, men, women, juniors, seniors
  const [view, setView] = useState('list'); // 'list' or 'calendar'

  useEffect(() => {
    loadTournaments();
  }, []);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      const response = await getUpcomingTournaments();
      
      if (response.success && response.data) {
        // Debug: log first tournament to see structure
        console.log('Sample tournament:', response.data[0]);
        console.log('Category field:', response.data[0]?.category);
        console.log('All categories:', response.data.map(t => t.category));
        
        // Sort by start date
        const sortedTournaments = response.data.sort((a, b) => 
          new Date(a.start_date) - new Date(b.start_date)
        );
        
        setTournaments(sortedTournaments);
      }
    } catch (err) {
      console.error('Error loading tournaments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start.toDateString() === end.toDateString()) {
      return formatDate(startDate);
    }
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Filter tournaments by category
  const filteredTournaments = tournaments.filter(tournament => {
    if (filter === 'all') return true;
    // Case-insensitive comparison
    return tournament.category?.toLowerCase() === filter.toLowerCase();
  });

  // Group tournaments by month
  const groupedByMonth = filteredTournaments.reduce((groups, tournament) => {
    const date = new Date(tournament.start_date);
    const monthYear = date.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
    
    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }
    groups[monthYear].push(tournament);
    return groups;
  }, {});

  const filterOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'mens_open', label: "Men's Open" },
    { value: 'mens_mid_am', label: "Men's Mid-Am" },
    { value: 'mens_senior', label: "Men's Senior" },
    { value: 'mens_super_senior', label: "Men's Super Senior" },
    { value: 'ladies_open', label: "Ladies Open" },
    { value: 'ladies_mid_am', label: "Ladies Mid-Am" },
    { value: 'ladies_senior', label: "Ladies Senior" },
    { value: 'ladies_super_senior', label: "Ladies Super Senior" },
    { value: 'junior_boys', label: 'Junior Boys' },
    { value: 'junior_girls', label: 'Junior Girls' },
    { value: 'mixed', label: 'Mixed' },
    { value: 'country_districts', label: 'Country Districts' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/fixturesHero.png" 
            alt="Fixtures" 
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-b from-fsgu-gray-900/60 via-fsgu-gray-900/70 to-fsgu-gray-900/90' 
              : 'bg-gradient-to-b from-black/40 via-black/50 to-black/70'
          }`}></div>
        </div>

        {/* Content */}
        <div className="container-custom px-4 relative z-10 text-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold font-cursive mb-8 md:mb-12 text-white leading-tight tracking-tight">
  Fixtures
</h1>
            
            {/* Rotating Text Section */}
            <div className="flex flex-wrap items-center justify-center gap-3 text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light mb-8">
              <span className="drop-shadow-lg text-white">Plan your</span>
              <RotatingText
                texts={[
                  'Season',
                  'Schedule',
                  'Calendar',
                  'Journey',
                ]}
                mainClassName={`font-cursive font-normal px-6 py-3 pb-4 rounded-2xl ${
                  isDarkMode
                    ? 'bg-fsgu-green-700/95 text-white'
                    : 'bg-fsgu-green-700 text-white'
                } drop-shadow-2xl`}
                splitLevelClassName="overflow-visible"
                staggerFrom="first"
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '-120%', opacity: 0 }}
                staggerDuration={0.03}
                transition={{ type: 'spring', damping: 30, stiffness: 400 }}
                rotationInterval={3000}
              />
            </div>

            <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-light max-w-3xl mx-auto leading-relaxed">
              Upcoming tournaments and events
            </p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 hidden md:block">
          <div className="animate-bounce">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </div>
      </section>

      {/* Fixtures Content */}
      <section className="section">
        <div className="container-custom">
          {/* View Toggle - Centered and Smaller */}
          <div className="flex items-center justify-center mb-8">
            <div className="inline-flex items-center space-x-2 p-1 rounded-lg bg-opacity-50 backdrop-blur-sm border ${
              isDarkMode
                ? 'bg-fsgu-gray-800/50 border-fsgu-gray-700'
                : 'bg-fsgu-silver-100/50 border-fsgu-silver-200'
            }">
              <button
                onClick={() => setView('list')}
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  view === 'list'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white shadow-sm'
                      : 'bg-fsgu-green-500 text-white shadow-sm'
                    : isDarkMode
                    ? 'text-fsgu-silver-400 hover:text-fsgu-silver-300'
                    : 'text-fsgu-silver-600 hover:text-fsgu-silver-700'
                }`}
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">List</span>
              </button>
              <button
                onClick={() => setView('calendar')}
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  view === 'calendar'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white shadow-sm'
                      : 'bg-fsgu-green-500 text-white shadow-sm'
                    : isDarkMode
                    ? 'text-fsgu-silver-400 hover:text-fsgu-silver-300'
                    : 'text-fsgu-silver-600 hover:text-fsgu-silver-700'
                }`}
              >
                <CalendarIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Calendar</span>
              </button>
            </div>
          </div>

          {/* Filter Buttons - Desktop: Two Rows, Mobile: Three Rows */}
          <div className="space-y-3 mb-12">
            {/* Mobile Row 1: All + Mixed + Country Districts */}
            <div className="flex sm:hidden flex-wrap items-center justify-center gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`flex-1 min-w-[100px] px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                  filter === 'all'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('mixed')}
                className={`flex-1 min-w-[100px] px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                  filter === 'mixed'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Mixed
              </button>
              <button
                onClick={() => setFilter('country_districts')}
                className={`flex-1 min-w-[100px] px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                  filter === 'country_districts'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Country
              </button>
            </div>

            {/* Mobile Row 2: All Men's + Junior Boys */}
            <div className="grid grid-cols-2 sm:hidden gap-2">
              <button
                onClick={() => setFilter('mens_open')}
                className={`px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                  filter === 'mens_open'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Men's Open
              </button>
              <button
                onClick={() => setFilter('mens_mid_am')}
                className={`px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                  filter === 'mens_mid_am'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Men's Mid-Am
              </button>
              <button
                onClick={() => setFilter('mens_senior')}
                className={`px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                  filter === 'mens_senior'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Men's Senior
              </button>
              <button
                onClick={() => setFilter('mens_super_senior')}
                className={`px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                  filter === 'mens_super_senior'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Men's Super Sr
              </button>
              <button
                onClick={() => setFilter('junior_boys')}
                className={`px-3 py-3 rounded-lg text-sm font-medium transition-all col-span-2 ${
                  filter === 'junior_boys'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Junior Boys
              </button>
            </div>

            {/* Mobile Row 3: All Ladies + Junior Girls */}
            <div className="grid grid-cols-2 sm:hidden gap-2">
              <button
                onClick={() => setFilter('ladies_open')}
                className={`px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                  filter === 'ladies_open'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Ladies Open
              </button>
              <button
                onClick={() => setFilter('ladies_mid_am')}
                className={`px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                  filter === 'ladies_mid_am'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Ladies Mid-Am
              </button>
              <button
                onClick={() => setFilter('ladies_senior')}
                className={`px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                  filter === 'ladies_senior'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Ladies Senior
              </button>
              <button
                onClick={() => setFilter('ladies_super_senior')}
                className={`px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                  filter === 'ladies_super_senior'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Ladies Super Sr
              </button>
              <button
                onClick={() => setFilter('junior_girls')}
                className={`px-3 py-3 rounded-lg text-sm font-medium transition-all col-span-2 ${
                  filter === 'junior_girls'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Junior Girls
              </button>
            </div>

            {/* Desktop Row 1: All + Men's + Junior Boys */}
            <div className="hidden sm:flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => setFilter('all')}
                className={`px-6 py-3 rounded-lg font-medium transition-all min-w-[140px] ${
                  filter === 'all'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                All Categories
              </button>
              <button
                onClick={() => setFilter('mens_open')}
                className={`px-6 py-3 rounded-lg font-medium transition-all min-w-[140px] ${
                  filter === 'mens_open'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Men's Open
              </button>
              <button
                onClick={() => setFilter('mens_mid_am')}
                className={`px-6 py-3 rounded-lg font-medium transition-all min-w-[140px] ${
                  filter === 'mens_mid_am'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Men's Mid-Am
              </button>
              <button
                onClick={() => setFilter('mens_senior')}
                className={`px-6 py-3 rounded-lg font-medium transition-all min-w-[140px] ${
                  filter === 'mens_senior'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Men's Senior
              </button>
              <button
                onClick={() => setFilter('mens_super_senior')}
                className={`px-6 py-3 rounded-lg font-medium transition-all min-w-[140px] ${
                  filter === 'mens_super_senior'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Men's Super Senior
              </button>
              <button
                onClick={() => setFilter('junior_boys')}
                className={`px-6 py-3 rounded-lg font-medium transition-all min-w-[140px] ${
                  filter === 'junior_boys'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Junior Boys
              </button>
            </div>

            {/* Desktop Row 2: Ladies + Junior Girls + Mixed + Country Districts */}
            <div className="hidden sm:flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => setFilter('ladies_open')}
                className={`px-6 py-3 rounded-lg font-medium transition-all min-w-[140px] ${
                  filter === 'ladies_open'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Ladies Open
              </button>
              <button
                onClick={() => setFilter('ladies_mid_am')}
                className={`px-6 py-3 rounded-lg font-medium transition-all min-w-[140px] ${
                  filter === 'ladies_mid_am'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Ladies Mid-Am
              </button>
              <button
                onClick={() => setFilter('ladies_senior')}
                className={`px-6 py-3 rounded-lg font-medium transition-all min-w-[140px] ${
                  filter === 'ladies_senior'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Ladies Senior
              </button>
              <button
                onClick={() => setFilter('ladies_super_senior')}
                className={`px-6 py-3 rounded-lg font-medium transition-all min-w-[140px] ${
                  filter === 'ladies_super_senior'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Ladies Super Senior
              </button>
              <button
                onClick={() => setFilter('junior_girls')}
                className={`px-6 py-3 rounded-lg font-medium transition-all min-w-[140px] ${
                  filter === 'junior_girls'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Junior Girls
              </button>
              <button
                onClick={() => setFilter('mixed')}
                className={`px-6 py-3 rounded-lg font-medium transition-all min-w-[140px] ${
                  filter === 'mixed'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Mixed
              </button>
              <button
                onClick={() => setFilter('country_districts')}
                className={`px-6 py-3 rounded-lg font-medium transition-all min-w-[140px] ${
                  filter === 'country_districts'
                    ? isDarkMode
                      ? 'bg-fsgu-green-500 text-white'
                      : 'bg-fsgu-green-500 text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
              >
                Country Districts
              </button>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-fsgu-green-500"></div>
              <p className={`mt-4 ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-600'}`}>
                Loading fixtures...
              </p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-12">
              <p className={`text-lg ${isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-600'}`}>
                {error}
              </p>
            </div>
          )}

          {/* Tournaments Grouped by Month */}
          {!loading && !error && view === 'list' && (
            <div className="space-y-12">
              {Object.keys(groupedByMonth).length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className={`w-16 h-16 mx-auto mb-4 ${
                    isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
                  }`} />
                  <p className={`text-lg ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-600'
                  }`}>
                    No upcoming fixtures found
                  </p>
                </div>
              ) : (
                Object.entries(groupedByMonth).map(([monthYear, monthTournaments]) => (
                  <div key={monthYear}>
                    {/* Month Header */}
                    <div className="flex items-center mb-6">
                      <h2 className={`text-2xl font-bold ${
                        isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                      }`}>
                        {monthYear}
                      </h2>
                      <div className={`flex-1 h-px ml-4 ${
                        isDarkMode ? 'bg-fsgu-gray-700' : 'bg-fsgu-silver-200'
                      }`}></div>
                    </div>

                    {/* Tournaments Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {monthTournaments.map((tournament) => (
                        <Link
                          key={tournament.id}
                          to={`/tournaments/${tournament.id}`}
                          className="card-hover group"
                        >
                          {/* Tournament Image */}
                          {tournament.image_url && (
                            <div className="aspect-video overflow-hidden rounded-t-lg mb-4 -mt-6 -mx-6">
                              <img
                                src={tournament.image_url}
                                alt={tournament.name}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                              />
                            </div>
                          )}

                          {/* Category Badge */}
                          {tournament.category && (
                            <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${
                              isDarkMode
                                ? 'bg-fsgu-green-500/20 text-fsgu-green-400'
                                : 'bg-fsgu-green-500/10 text-fsgu-green-600'
                            }`}>
                              {tournament.category}
                            </div>
                          )}

                          {/* Tournament Name */}
                          <h3 className={`text-lg font-semibold mb-3 line-clamp-2 ${
                            isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                          }`}>
                            {tournament.tournament_name}
                          </h3>

                          {/* Tournament Details */}
                          <div className="space-y-2 mb-4">
                            {/* Date */}
                            <div className="flex items-center text-sm">
                              <Calendar className={`w-4 h-4 mr-2 ${
                                isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                              }`} />
                              <span className={
                                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                              }>
                                {formatDateRange(tournament.start_date, tournament.end_date)}
                              </span>
                            </div>

                            {/* Location */}
                            {tournament.venue_name && (
                              <div className="flex items-center text-sm">
                                <MapPin className={`w-4 h-4 mr-2 ${
                                  isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                                }`} />
                                <span className={
                                  isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                                }>
                                  {tournament.venue_name}
                                  {tournament.venue_city && `, ${tournament.venue_city}`}
                                </span>
                              </div>
                            )}

                            {/* Max Participants */}
                            {tournament.max_participants && (
                              <div className="flex items-center text-sm">
                                <Users className={`w-4 h-4 mr-2 ${
                                  isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                                }`} />
                                <span className={
                                  isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                                }>
                                  Max {tournament.max_participants} participants
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Status Badge */}
                          {tournament.status === 'registration_open' && (
                            <div className={`flex items-center justify-between ${
                              isDarkMode
                                ? 'text-fsgu-green-400'
                                : 'text-fsgu-green-600'
                            }`}>
                              <span className="text-sm font-medium">Registration Open</span>
                              <ChevronRight className="w-5 h-5" />
                            </div>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Calendar View */}
          {!loading && !error && view === 'calendar' && (
            <TournamentsCalendar tournaments={filteredTournaments} />
          )}

          {/* View All Tournaments Button */}
          {!loading && !error && filteredTournaments.length > 0 && (
            <div className="text-center mt-12">
              <Link
                to="/tournaments"
                className="btn btn-primary inline-flex items-center"
              >
                View All Tournaments
                <ChevronRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default FixturesPage;